import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { MetricCatalogResponse, MetricCustomization } from "@/interfaces/v2Analytics";
import { getHumanMetricSourceMetrics } from "@/store/slices/metricsCatalog/catalogSelectors";
import { setMetricsCatalog } from "@/store/slices/metricsCatalog/metricsCatalogSlice";

/**
 * A human-comparison metric (awareness, consideration, …) shaped for the
 * same `{ ids, entities }` catalog lookup that `aiModels` already uses.
 * `key` is the catalog `metricKey` the API expects on `humanMetricKeys`.
 */
export interface HumanMetric {
  key: string;
  label: string;
  description?: string;
  questionText?: string;
  /**
   * Present when the measure uses the account's own definition; it applies to a
   * selection led by one of its `marketIds`.
   */
  customization?: MetricCustomization;
  moduleKey: string;
  marketIds: number[];
}

/** Catalog modules that back the AI Visibility human-metric picker. */
const HUMAN_METRIC_SOURCE_MODULES = ["conversion", "equity"] as const;

/** Human metrics currently active for the AI Visibility picker, in display order. */
export const ALLOWED_HUMAN_METRIC_KEYS = [
  "awareness",
  "familiarity",
  "momentum",
  "consideration",
  "usage",
  "recommend",
] as const;

export interface HumanMetricsState {
  ids: string[];
  entities: Record<string, HumanMetric>;
}

const initialState: HumanMetricsState = { ids: [], entities: {} };

/** Catalog conversion/equity metrics narrowed down to `ALLOWED_HUMAN_METRIC_KEYS`. */
function getAllowedHumanMetrics(
  catalog: MetricCatalogResponse,
): HumanMetric[] {
  const byKey = new Map<string, HumanMetric>();
  for (const moduleKey of HUMAN_METRIC_SOURCE_MODULES) {
    for (const metric of getHumanMetricSourceMetrics(catalog, moduleKey)) {
      if (byKey.has(metric.metricKey)) continue;
      byKey.set(metric.metricKey, {
        key: metric.metricKey,
        label: metric.label,
        description: metric.description,
        questionText: metric.questionText,
        ...(metric.customization?.customized ? { customization: metric.customization } : {}),
        moduleKey,
        marketIds: metric.marketIds ?? [],
      });
    }
  }
  return ALLOWED_HUMAN_METRIC_KEYS.map((key) => byKey.get(key)).filter(
    (metric): metric is HumanMetric => metric !== undefined,
  );
}

const humanMetricsSlice = createSlice({
  name: "humanMetrics",
  initialState,
  reducers: {
    /**
     * Replace the catalog. Null/empty leaves the existing state in place
     * rather than clearing the dropdown.
     */
    setHumanMetrics: (
      state,
      action: PayloadAction<HumanMetric[] | null>,
    ) => {
      const metrics = action.payload?.filter((metric) => Boolean(metric?.key));
      if (!metrics || metrics.length === 0) return;
      state.ids = metrics.map((metric) => metric.key);
      state.entities = Object.fromEntries(
        metrics.map((metric) => [metric.key, metric]),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      setMetricsCatalog,
      (state, action: PayloadAction<MetricCatalogResponse | null>) => {
        if (!action.payload) return;
        const metrics = getAllowedHumanMetrics(action.payload);
        if (metrics.length === 0) return;
        state.ids = metrics.map((metric) => metric.key);
        state.entities = Object.fromEntries(
          metrics.map((metric) => [metric.key, metric]),
        );
      },
    );
  },
});

export const { setHumanMetrics } = humanMetricsSlice.actions;
export default humanMetricsSlice.reducer;
